import { useState, useEffect } from "react";
import {
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  Typography,
  Divider,
  IconButton,
  Popover,
  Box,
  Alert,
  Skeleton,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import InvoiceDateRangePicker from "components/Time/InvoiceDateRangePicker";
import { CalendarMonth, ScheduleSend } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Loading from "components/Loading";
import { useParams } from "react-router-dom";
import { fetchSupabaseClients } from "store/ClientSlice";
import {
  // fetchSupabaseInvoiceRecords,
  fetchOneSupabaseInvoice,
  updateSupabaseInvoice,
  calculateInvoice,
} from "store/InvoiceSlice";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import moment from "moment";
import InfoIcon from "@mui/icons-material/Info";
import InvoicePanel from "./components/InvoicePanel";
import DeleteInvoiceModal from "./components/DeleteInvoiceModal";
import RecalculateInvoiceModal from "./components/RecalculateInvoiceModal";

function InvoiceEditForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useSupabaseAuth();
  const theme = useTheme();
  const { id: invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Add new state for display dates
  const [displayDates, setDisplayDates] = useState({
    from_date: "",
    to_date: "",
  });

  // Add state for date picker view toggle
  const [datePickerView, setDatePickerView] = useState("inputs"); // "calendar" or "inputs"

  // Load the invoices in case we need it.
  const clients = useSelector((state) => state.clientReducer.clients);

  const loading = useSelector((state) => state.invoiceReducer.loading);
  const error = useSelector((state) => state.clientReducer.error);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);

  // Multiple popover states
  const [popoverStates, setPopoverStates] = useState({
    reference: null,
    invoiceNumber: null,
    notes: null,
    // Add more as needed
  });

  // Utility functions for date formatting
  const formatDateForDisplay = (date) => {
    if (!date) return "";
    // Handle YYYY-MM-DD strings directly, or parse as Date if needed
    // moment can parse both formats without timezone issues
    return moment(date).format("YYYY-MM-DD");
  };

  const formatDateForStorage = (date, isEndDate = false) => {
    if (!date) return "";
    // Format as YYYY-MM-DD (ISO date format) without timezone or time component
    return moment(date).format("YYYY-MM-DD");
  };

  // Modified useEffect to handle date formatting when loading data
  useEffect(() => {
    if (userId) {
      dispatch(fetchSupabaseClients({ userId }));
      dispatch(fetchOneSupabaseInvoice({ id: invoiceId, userId })).then(
        (fetchedInvoice) => {
          if (fetchedInvoice.error) {
            setInvoice(null);
          } else {
            console.log("fetchedInvoice", fetchedInvoice);

            console.log("fetchedInvoice.payload", fetchedInvoice.payload);
            const invoiceData = {
              ...fetchedInvoice.payload,
              invoice_emails: fetchedInvoice.payload.invoice_emails || [],
              invoice_processing: fetchedInvoice.payload.invoice_processing || "manual",
            };
            setInvoice(invoiceData);
            // Set display dates when invoice loads
            setDisplayDates({
              from_date: formatDateForDisplay(fetchedInvoice.payload.from_date),
              to_date: formatDateForDisplay(fetchedInvoice.payload.to_date),
            });
          }
        }
      );
    }
  }, [dispatch, invoiceId, userId]);

  // Handle date changes from CalendarDateRangePicker
  const handleCalendarDateChange = (values) => {
    console.log("Calendar date change:", values);
    const [fromDate, toDate] = values;

    // Update the display dates for the input fields
    setDisplayDates({
      from_date: formatDateForDisplay(fromDate),
      to_date: formatDateForDisplay(toDate),
    });

    // Update the invoice state with the formatted dates for storage
    setInvoice((prev) => ({
      ...prev,
      from_date: formatDateForStorage(fromDate),
      to_date: formatDateForStorage(toDate, true),
    }));
  };

  // Handle individual text field date changes
  const handleDateFieldChange = (event) => {
    const { name, value } = event.target;
    console.log("text field change", { name, value });

    // Update display dates
    setDisplayDates((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Pass the YYYY-MM-DD string directly to formatDateForStorage
    // Don't use new Date() as it interprets as UTC and causes timezone shifts
    // formatDateForStorage can handle YYYY-MM-DD strings directly
    const dateValue = value || null;

    // Update the invoice state
    setInvoice((prev) => ({
      ...prev,
      [name]: formatDateForStorage(dateValue, name === "to_date"),
    }));
  };

  // Handle date picker view toggle
  const handleDatePickerViewChange = (event, newView) => {
    if (newView !== null) {
      setDatePickerView(newView);
    }
  };

  // Handle Today button click for input fields
  const handleTodayClick = () => {
    const today = new Date();
    const todayFormatted = formatDateForDisplay(today);

    setDisplayDates((prev) => ({
      ...prev,
      from_date: todayFormatted,
    }));

    setInvoice((prev) => ({
      ...prev,
      from_date: formatDateForStorage(today, false),
    }));
  };

  // Handle Clear button click for input fields
  const handleClearClick = () => {
    setDisplayDates({
      from_date: "",
      to_date: "",
    });

    setInvoice((prev) => ({
      ...prev,
      from_date: "",
      to_date: "",
    }));
  };

  // Keep the regular handleChange for non-date fields
  const handleChange = (event) => {
    const { name, value } = event.target;
    // Only handle non-date fields
    if (name !== "from_date" && name !== "to_date") {
      setInvoice((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    // Extract only the fields that should be saved to the database
    // Exclude the 'client' object which is from a join query
    const {
      client,
      id,
      uid,
      events,
      totals,
      created_at,
      updated_at,
      calculated_at,
      ...dataToSave
    } = invoice;

    // Ensure client_id is a string if it exists
    if (dataToSave.client_id) {
      dataToSave.client_id = dataToSave.client_id.toString();
    }

    dispatch(updateSupabaseInvoice({ id: invoice.id, data: dataToSave }))
      .then(() => {
        navigate(`/revenue/${invoice.id}`);
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
      });
  };

  // Generic popover handlers for multiple popovers
  const handlePopoverClick = (event, popoverKey) => {
    setPopoverStates((prev) => ({
      ...prev,
      [popoverKey]: event.currentTarget,
    }));
  };

  const handlePopoverClose = (popoverKey) => {
    setPopoverStates((prev) => ({
      ...prev,
      [popoverKey]: null,
    }));
  };

  const handleChangeClient = (event) => {
    const { name, value } = event.target;
    setInvoice({ ...invoice, [name]: value });
  };

  // Handle invoice deletion
  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleCalculate = () => {
    setRecalculateModalOpen(true);
  };

  const handleConfirmRecalculate = () => {
    dispatch(calculateInvoice({ id: invoice.id })).then((result) => {
      if (calculateInvoice.fulfilled.match(result)) {
        // Refresh the invoice data after calculation
        dispatch(fetchOneSupabaseInvoice(invoice.id));
      }
    });
  };

  if (loading) {
    return <Loading text="loading ..." />;
  }

  return (
    <Box maxWidth="md" sx={{ py: 2, mx: "auto" }}>
      {invoice && <InvoicePanel invoice={invoice} />}
      {!loading && invoice === null && (
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 3,
                height: "300px",
              }}
            >
              <Typography variant="h4" component="h1">
                Uh oh!
              </Typography>
              <Typography variant="h5" component="h2">
                No Invoice Found
              </Typography>
              <Typography variant="body1">
                The invoice you are looking for is either no longer available or
                is misconfigured.
              </Typography>
              <Button
                component={Link}
                to="/revenue"
                variant="contained"
                color="primary"
              >
                Back to Invoices
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {!loading && invoice !== null && (
        <>
          <Card>
            <CardHeader title="Edit Revenue" />
            <CardContent>
              {!invoice ? (
                // Skeleton loading state
                <Box>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={24}
                    sx={{ mb: 3 }}
                  />
                  <Grid container spacing={4}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Stack spacing={1}>
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="rectangular" height={56} />
                      </Stack>
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Skeleton variant="text" width="30%" height={20} />
                        <Skeleton variant="rectangular" height={56} />
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Stack spacing={1}>
                        <Skeleton variant="text" width="35%" height={20} />
                        <Skeleton variant="rectangular" height={56} />
                      </Stack>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 4 }} />
                  <Grid container spacing={4}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Stack spacing={3}>
                        <Box>
                          <Skeleton variant="text" width="25%" height={20} />
                          <Skeleton
                            variant="rectangular"
                            height={56}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                        <Box>
                          <Grid container spacing={2}>
                            <Grid
                              size={{
                                xs: 12,
                                md: 6,
                              }}
                            >
                              <Skeleton
                                variant="text"
                                width="30%"
                                height={20}
                              />
                              <Skeleton variant="rectangular" height={56} />
                            </Grid>
                            <Grid
                              size={{
                                xs: 12,
                                md: 6,
                              }}
                            >
                              <Skeleton
                                variant="text"
                                width="25%"
                                height={20}
                              />
                              <Skeleton variant="rectangular" height={56} />
                            </Grid>
                          </Grid>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Stack spacing={1}>
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="rectangular" height={80} />
                      </Stack>
                    </Grid>
                    <Grid size={12}>
                      <Skeleton variant="rectangular" height={48} />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <>
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack direction="column" spacing={3}>
                      {/* Client */}
                      <Box>
                        <InputLabel>
                          Client for this{" "}
                          {invoice?.type === "project_price"
                            ? "Project"
                            : (invoice?.type === "retainer_monthly" || invoice?.type === "retainer_rolling")
                              ? "Retainer"
                              : "Invoice"}
                        </InputLabel>
                        <Select
                          name="client_id"
                          value={invoice.client_id || ""}
                          onChange={handleChangeClient}
                          fullWidth
                          required
                        >
                          <MenuItem value="">Select...</MenuItem>
                          {clients.map((row) => (
                            <MenuItem key={row.id} value={row.id}>
                              {row.name || ""} ({row.project})
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>

                      {/* Reference and Invoice Number on same line */}
                      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ flex: '0 0 70%', width: { xs: '100%', sm: '70%' } }}>
                          <InputLabel>Reference Name</InputLabel>
                          <TextField
                            sx={{ mt: 0 }}
                            name="name"
                            value={invoice.name}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  aria-describedby="reference-popover"
                                  onClick={(event) =>
                                    handlePopoverClick(event, "reference")
                                  }
                                  size="small"
                                  style={{ marginLeft: "4px" }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              ),
                            }}
                            fullWidth
                            required
                            placeholder="Reference Name"
                          />

                          <Popover
                            id="reference-popover"
                            open={Boolean(popoverStates.reference)}
                            anchorEl={popoverStates.reference}
                            onClose={() => handlePopoverClose("reference")}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Typography sx={{ p: 2 }}>
                              Enter a human readable name that will appear on reports
                              etc.
                            </Typography>
                          </Popover>
                        </Box>
                        <Box sx={{ flex: '0 0 30%', width: { xs: '100%', sm: '30%' } }}>
                          <InputLabel>
                            {invoice?.type === "project_price"
                              ? "Project Number"
                              : (invoice?.type === "retainer_monthly" || invoice?.type === "retainer_rolling")
                                ? "Retainer Number"
                                : "Invoice Number"}
                          </InputLabel>
                          <TextField
                            sx={{ mt: 0 }}
                            type="number"
                            name="invoice_num"
                            value={invoice.invoice_num || ""}
                            onChange={handleChange}
                            placeholder="Leave empty to auto-generate"
                            fullWidth
                            helperText={
                              invoice?.type === "project_price"
                                ? "Leave empty to automatically generate the next project number"
                                : (invoice?.type === "retainer_monthly" || invoice?.type === "retainer_rolling")
                                  ? "Leave empty to automatically generate the next retainer number"
                                  : "Leave empty to automatically generate the next invoice number"
                            }
                            inputProps={{
                              min: 1,
                              step: 1
                            }}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  aria-describedby="invoice-number-popover"
                                  onClick={(event) =>
                                    handlePopoverClick(event, "invoiceNumber")
                                  }
                                  size="small"
                                  style={{ marginLeft: "4px" }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              ),
                            }}
                          />

                          <Popover
                            id="invoice-number-popover"
                            open={Boolean(popoverStates.invoiceNumber)}
                            anchorEl={popoverStates.invoiceNumber}
                            onClose={() =>
                              handlePopoverClose("invoiceNumber")
                            }
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Typography sx={{ p: 2 }}>
                              {invoice?.type === "project_price"
                                ? "Enter a custom project number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Project numbers must be unique per account."
                                : (invoice?.type === "retainer_monthly" || invoice?.type === "retainer_rolling")
                                  ? "Enter a custom retainer number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Retainer numbers must be unique per account."
                                  : "Enter a custom invoice number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Invoice numbers must be unique per account."}
                            </Typography>
                          </Popover>
                        </Box>
                      </Box>

                      <Divider />

                      {/* Date Options */}
                      <Box>
                        <InvoiceDateRangePicker
                          invoice={invoice}
                          displayDates={displayDates}
                          datePickerView={datePickerView}
                          onDatePickerViewChange={handleDatePickerViewChange}
                          onCalendarDateChange={handleCalendarDateChange}
                          onDateFieldChange={handleDateFieldChange}
                          onTodayClick={handleTodayClick}
                          onClearClick={handleClearClick}
                        />
                      </Box>
                    </Stack>

                    {invoice.from_date && invoice.to_date && (
                      <Box sx={{ mt: 3 }}>
                        <Alert
                          variant="outlined"
                          icon={<CalendarMonth />}
                          sx={{ mb: 2 }}
                        >
                          This{" "}
                          {invoice?.type === "project_price"
                            ? "project"
                            : (invoice?.type === "retainer_monthly" || invoice?.type === "retainer_rolling")
                              ? "retainer"
                              : "invoice"}{" "}
                          spans{" "}
                          <b>
                            {moment(invoice.to_date).diff(
                              moment(invoice.from_date),
                              "days"
                            )}{" "}
                            days{" "}
                          </b>
                          from {moment(invoice.from_date).format("dddd MM/DD/YY")}
                          &nbsp; to &nbsp;
                          {moment(invoice.to_date).format("dddd MM/DD/YY")}.
                        </Alert>
                        <Alert variant="outlined" icon={<ScheduleSend />}>
                          <i>
                            Expect this engagement to close
                            {moment(invoice.to_date).diff(moment(), "days") > 0 &&
                              " in"}{" "}
                            <b>
                              {Math.abs(
                                moment(invoice.to_date).diff(moment(), "days")
                              )}
                              {Math.abs(
                                moment(invoice.to_date).diff(moment(), "days")
                              ) > 1
                                ? " days"
                                : " day"}
                              {moment(invoice.to_date).diff(moment(), "days") <
                                0 && " ago"}
                              .
                            </b>
                          </i>
                        </Alert>
                      </Box>
                    )}


                    <Divider />

                    {/* Notes */}
                    <Box sx={{ mt: 3 }}>
                      <InputLabel>Internal Notes</InputLabel>
                      <TextField
                        value={invoice.notes || ""}
                        name="notes"
                        onChange={handleChange}
                        fullWidth
                        placeholder="Add any additional notes."
                        multiline
                        rows={3}
                      />
                    </Box>



                    <Divider />

                    {/* Invoice Status Fields */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
                        Invoice Status
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <InputLabel>Sent to Client</InputLabel>
                          <Select
                            name="sent"
                            value={invoice.sent || "no"}
                            onChange={handleChange}
                            fullWidth
                            required
                          >
                            <MenuItem value="no">No</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                          </Select>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <InputLabel>Payment Status</InputLabel>
                          <Select
                            name="paid"
                            value={invoice.paid || ""}
                            onChange={handleChange}
                            fullWidth
                            required
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="no">No (not yet)</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="na">NA</MenuItem>
                          </Select>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="status"
                            value={invoice.status || ""}
                            onChange={handleChange}
                            fullWidth
                            required
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                          </Select>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Invoice Processing Options */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
                        How will this engagement be processed?
                      </Typography>
                      <RadioGroup
                        name="invoice_processing"
                        value={invoice.invoice_processing || "manual"}
                        onChange={handleChange}
                        sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            border: 2,
                            borderColor: invoice.invoice_processing === "manual" ? "primary.main" : "divider",
                            borderRadius: 2,
                            p: 2,
                            cursor: "pointer",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "action.hover",
                            },
                          }}
                          onClick={() => handleChange({ target: { name: "invoice_processing", value: "manual" } })}
                        >
                          <FormControlLabel
                            value="manual"
                            control={<Radio />}
                            label={
                              <Typography variant="h6">
                                Manual Processing? (Default)
                              </Typography>
                            }
                            sx={{ m: 0, mb: 1, width: "100%" }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                            This invoice will be held for manual review and closing. The online will
                            close but nothing will be sent to the client. You will need to manually send
                            the invoice to the client.
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            border: 2,
                            borderColor: invoice.invoice_processing === "invoice" ? "primary.main" : "divider",
                            borderRadius: 2,
                            p: 2,
                            cursor: "pointer",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "action.hover",
                            },
                          }}
                          onClick={() => handleChange({ target: { name: "invoice_processing", value: "invoice" } })}
                        >
                          <FormControlLabel
                            value="invoice"
                            control={<Radio />}
                            label={
                              <Typography variant="h6">
                                Automatically Process?
                              </Typography>
                            }
                            sx={{ m: 0, mb: 1, width: "100%" }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                            This invoice will be automatically processed and closed. This will
                            send the invoice to the client via email once the invoice is created
                            on the day after the invoice cycle ends.
                          </Typography>
                        </Box>
                      </RadioGroup>
                    </Box>

                    {/* Invoice Email Recipients */}
                    {invoice.invoice_processing === "invoice" && (
                      <Box sx={{ mt: 2 }}>
                        <InputLabel>Invoice Email Recipients *</InputLabel>
                        <TextField
                          name="invoice_emails"
                          value={Array.isArray(invoice.invoice_emails)
                            ? invoice.invoice_emails.join('\n')
                            : (invoice.invoice_emails || '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Convert textarea value (newline-separated) to array, filter out empty strings
                            const emailArray = value
                              .split('\n')
                              .map(email => email.trim())
                              .filter(email => email.length > 0);
                            handleChange({ target: { name: "invoice_emails", value: emailArray } });
                          }}
                          fullWidth
                          multiline
                          rows={4}
                          required
                          placeholder="Enter email addresses, one per line"
                          helperText="Enter the email addresses that will receive this invoice. One email per line."
                        />
                        {invoice.client_id && (() => {
                          const selectedClient = clients.find((client) => client.id === invoice.client_id);
                          const clientEmails = selectedClient?.invoice_emails || [];
                          if (clientEmails.length > 0 && (!invoice.invoice_emails || invoice.invoice_emails.length === 0)) {
                            return (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Suggested: {clientEmails.join(', ')}
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    )}

                    {/* Submit Button */}
                    <Box sx={{ mt: 3 }}>
                      <Button
                        size="large"
                        variant="contained"
                        color="primary"
                        type="submit"
                        fullWidth
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
      <DeleteInvoiceModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        invoice={invoice}
      />
      <RecalculateInvoiceModal
        open={recalculateModalOpen}
        onClose={() => setRecalculateModalOpen(false)}
        onConfirm={handleConfirmRecalculate}
        invoice={invoice}
      />
    </Box>
  );
}

export default InvoiceEditForm;
